import { CostBreakdownData } from '@/features/dashboard/types';
import {
  CarrierCodeType,
  CurrencyCodeType,
  DimensionUnitType,
  ExportReasonType,
  InProgress,
  PackageType,
  PrintLabelSize,
  SignatureType,
  WeightUnitType,
} from '@/features/shipment/types';

export type CarriersType = Array<keyof typeof CarrierCodeType>;

export type DimUnitsOptionType = Array<keyof typeof DimensionUnitType>;

export type WeightUnitsOptionType = Array<keyof typeof WeightUnitType>;

export type PackageOptionsType = Array<{ text: string, value: keyof typeof PackageType }>;

export type SignatureOptionsType = Array<{ text: string, value: keyof typeof SignatureType }>;

export type ReasonsForExportOptionsType = Array<{
  text: string,
  value: keyof typeof ExportReasonType
}>;

export const defaultOffsetLimit = 20;

export const safeListedCountryCodes = ['CA', 'US'];

export const monerisEnabledCountryCodes = [
  'CA', // Canada (always supported)
  'NZ', // New Zealand
  'AU', // Australia
  'FR', // France
  // 'EN', // England (part of UK)
  // 'SF', // Scotland (part of UK)
  // 'WL', // Wales (part of UK)
  // 'NB', // Northern Ireland (part of UK)
  'GB', // United Kingdom
  'IN', // India
  'CN', // China
  'ES', // Spain
  'DE', // Germany
  'MX', // Mexico
  'AE', // UAE
  'NL', // Netherlands
  'BE', // Belgium
  'SE', // Sweden
  'NO', // Norway
  'DK', // Denmark
  'FI', // Finland
  'CH', // Switzerland
  'AT', // Austria
];

export const colors = [
  'red', 'blue', 'indigo', 'pink', 'purple', 'deep-purple', 'teal',
  'cyan', 'green', 'orange', 'brown', 'blue-grey',
];

export const primaryCurrency: Array<keyof typeof CurrencyCodeType> = ['CAD', 'USD'];

export enum FreightLimit {
  'Lbs' = 150,
  'Kgs' = 68
}

export const carriers: CarriersType = [
  'UPS',
  'FedEx',
  'Purolator',
  'DHL',
  'Canpar',
  'ICS',
  'PTC',
  'Canadapost',
  'Loomis',
  'GLS',
  'Trexity',
  'USPS',
];

export const packageTypes: PackageOptionsType = [
  {
    text: 'My Package',
    value: 'MyPackage',
  },
  {
    text: 'Envelope',
    value: 'Envelope',
  },
  {
    text: 'Pak',
    value: 'Pak',
  },
  {
    text: 'Skid / Pallet',
    value: 'Pallet',
  },
];

export const dimUnitsItems: DimUnitsOptionType = ['Inches', 'CM'];

export const weightUnitsItems: WeightUnitsOptionType = ['Lbs', 'Kgs'];

export const earliestPickupTimes = [
  'It\'s Ready Now',
  '6:00 AM',
  '6:30 AM',
  '7:00 AM',
  '7:30 AM',
  '8:00 AM',
  '8:30 AM',
  '9:00 AM',
  '9:30 AM',
  '10:00 AM',
  '10:30 AM',
  '11:00 AM',
  '11:30 AM',
  '12:00 PM',
  '12:30 PM',
  '1:00 PM',
  '1:30 PM',
  '2:00 PM',
  '2:30 PM',
  '3:00 PM',
  '3:30 PM',
  '4:00 PM',
  '4:30 PM',
  '5:00 PM',
  '5:30 PM',
  '6:00 PM',
  '6:30 PM',
];

export const latestPickupTimes = [
  '12:00 PM',
  '12:30 PM',
  '1:00 PM',
  '1:30 PM',
  '2:00 PM',
  '2:30 PM',
  '3:00 PM',
  '3:30 PM',
  '4:00 PM',
  '4:30 PM',
  '5:00 PM',
  '5:15 PM',
  '5:30 PM',
  '5:45 PM',
  '6:00 PM',
  '6:30 PM',
  '7:00 PM',
  '7:30 PM',
];

export const pickUpLocations = [
  {
    text: 'Front Door',
    value: 'FrontDoor',
  },
  {
    text: 'Back Door',
    value: 'BackDoor',
  },
  {
    text: 'Side Door',
    value: 'SideDoor',
  },
  {
    text: 'Shipping',
    value: 'Shipping',
  },
  {
    text: 'Receiving',
    value: 'Receiving',
  },
  {
    text: 'Reception',
    value: 'Reception',
  },
  {
    text: 'Office',
    value: 'Office',
  },
  {
    text: 'Mail Room',
    value: 'MailRoom',
  },
  {
    text: 'Garage',
    value: 'Garage',
  },
  {
    text: 'Upstairs',
    value: 'Upstairs',
  },
  {
    text: 'Downstairs',
    value: 'Downstairs',
  },
  {
    text: 'Guard Room',
    value: 'GuardRoom',
  },
  {
    text: 'Warehouse',
    value: 'Warehouse',
  },
];

export const signatureOptions: SignatureOptionsType = [
  {
    text: 'None',
    value: 'None',
  },
  {
    text: 'Signature Required',
    value: 'SignatureRequired',
  },
  {
    text: 'Adult Signature Required',
    value: 'AdultSignatureRequired',
  },
];

export const reasonsForExport: ReasonsForExportOptionsType = [
  {
    text: 'Permanent / Sold',
    value: 'Permanent',
  },
  {
    text: 'Temporary',
    value: 'Temporary',
  },
  {
    text: 'Gift',
    value: 'Gift',
  },
  {
    text: 'Repair / Warranty',
    value: 'Repair',
  },
  {
    text: 'Commercial Sample',
    value: 'CommercialSample',
  },
  {
    text: 'Personal Effects',
    value: 'PersonalEffects',
  },
  {
    text: 'Return Shipment',
    value: 'Return',
  },
];

export const quantityUnits = [
  'Each',
  'Box',
  'Bag',
  'Barrel',
  'Bunch',
  'Bundle',
  'Canister',
  'Crate',
  'Carton',
  'Cylinder',
  'Dozen',
  'Envelope',
  'Pallet',
  'Piece',
  'Pieces',
  'Package',
  'Pair',
  'Pairs',
  'Set',
];

export const labelSizes: (keyof typeof PrintLabelSize)[] = [
  'Standard',
  'Thermal',
];

export const defaultInProgressData: InProgress = {
  carrierCode: '',
  serviceLevel: '',
  lastUpdated: '',
  shipDate: '',
  saturdayDelivery: false,
  documentsOnly: false,
  fromAddress: {
    company: '',
    contact: '',
    phone: '',
    addr1: '',
    addr2: '',
    addr3: '',
    countryCode: '',
    postalCode: '',
    province: '',
    city: '',
    residential: false,
    isInside: false,
    isTailGate: false,
    isTradeShow: false,
    isLimitedAccess: false,
  },
  toAddress: {
    company: '',
    contact: '',
    phone: '',
    addr1: '',
    addr2: '',
    addr3: '',
    countryCode: '',
    postalCode: '',
    province: '',
    city: '',
    emails: [],
    residential: false,
    isInside: false,
    isTailGate: false,
    isTradeShow: false,
    isLimitedAccess: false,
  },
  soldTo: {
    entityMatch: 'same',
    address: {
      contact: '',
      company: '',
      phone: '',
      addr1: '',
      addr2: '',
      addr3: '',
      countryCode: '',
      postalCode: '',
      province: '',
      city: '',
      emails: [],
    },
  },
  emailNotifications: [],
  references: [],
  comments: null,
  signatureOptions: '',
  billing: {
    billingType: 'Prepaid',
    account: '',
  },
  temporaryPackage: {
    packageType: 'MyPackage',
    weight: '',
    length: '',
    width: '',
    height: '',
    dimUnits: '',
    weightUnits: '',
    insurance: '',
    isAdditionalHandling: false,
    description: '',
    signatureOptions: '',
  },
  packages: [],
  ecommerce: {
    id: 0,
    platformType: '',
    shopDetails: {
      name: '',
      url: '',
    },
    uniqueOrderId: '',
    orderId: '',
    fulfillOrder: false,
    storeNotification: false,
  },
  commercialInvoice: {
    customsParts: [],
    reasonForExport: '',
    generalDescriptionOfGoods: '',
    currencyCode: '',
    remarks: '',
    statement: '',
  },
  isReturnShipment: false,
  isLabelFree: false,
  isBoxFree: false,
  uploadDocuments: [],
};

export const defaultEstimateData = {
  fromAddress: {
    addr1: '',
    countryCode: '',
    postalCode: '',
    city: '',
    isInside: false,
    isLimitedAccess: false,
    isTailGate: false,
    isTradeShow: false,
  },
  toAddress: {
    addr1: '',
    countryCode: '',
    postalCode: '',
    city: '',
    residential: false,
    isInside: false,
    isLimitedAccess: false,
    isTailGate: false,
    isTradeShow: false,
  },
  signatureOptions: '',
  packages: [],
  isDocumentsOnly: false,
};

export interface CarrierFilterType {
  name: string,
  show: boolean,
  levels: Array<{name: string, show: boolean, value: string }>,
}

enum PeriodItemValue {
  'Last7Days',
  'Last30Days',
  'Last60Days',
  'Last90Days',
  'Last180Days',
  'LastYear',
  'Custom'
}

export type PeriodItemValueType = keyof typeof PeriodItemValue;

export interface PeriodItemType {
  value: PeriodItemValueType,
  displayName: string
}

export const periodItems: Array<PeriodItemType> = [
  {
    value: 'Last7Days',
    displayName: 'Last 7 days',
  },
  {
    value: 'Last30Days',
    displayName: 'Last 30 days',
  },
  {
    value: 'Last60Days',
    displayName: 'Last 60 days',
  },
  {
    value: 'Last90Days',
    displayName: 'Last 90 days',
  },
  {
    value: 'Last180Days',
    displayName: 'Last 180 days',
  },
  {
    value: 'LastYear',
    displayName: 'Last year',
  },
];

export const defaultCostBreakdownData: CostBreakdownData = {
  avgPackageCountPerDay: 0,
  avgSpendPerDay: 0,
  avgSpendPerPackage: 0,
  avgSpendPerShipment: 0,
  totalPackageCount: 0,
  totalSavings: 0,
  totalSavingsPercentage: 0,
  totalSpend: 0,
  breakdownPerCarrier: [],
};

const queryPackageParams = [
  {
    parameter: 'Signature',
    required: false,
    defaultValue: 'None',
  },
  {
    parameter: 'isDocumentsOnly',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'PT1',
    required: true,
  },
  {
    parameter: 'Weight1',
    required: true,
    defaultValue: '0',
  },
  {
    parameter: 'L1',
    required: false,
    defaultValue: '0',
  },
  {
    parameter: 'W1',
    required: false,
    defaultValue: '0',
  },
  {
    parameter: 'H1',
    required: false,
    defaultValue: '0',
  },
  {
    parameter: 'DV1',
    required: false,
    defaultValue: '0',
  },
  {
    parameter: 'AH1',
    required: false,
    defaultValue: 'false',
  },
];

export const getEstimateQueryParams = [
  {
    parameter: 'FromCC',
    required: true,
  },
  {
    parameter: 'FromPC',
    required: true,
  },
  {
    parameter: 'FromCity',
    requiredForInternational: true,
    required: true,
  },
  {
    parameter: 'FromIsInside',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'FromIsLimitedAccess',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'FromIsTailGate',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'FromIsTradeShow',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'ToCC',
    required: true,
  },
  {
    parameter: 'ToPC',
    required: true,
  },
  {
    parameter: 'ToCity',
    required: true,
    requiredForInternational: true,
  },
  {
    parameter: 'ToResidential',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'ToIsInside',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'ToIsLimitedAccess',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'ToIsTailGate',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'ToIsTradeShow',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'ToAppointment',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'ToAppointmentDate',
    required: false,
  },
  {
    parameter: 'ToAppointmentTime',
    required: false,
  },
  ...queryPackageParams,
];

export const createShipmentQueryParams = [
  {
    parameter: 'FromCompany',
    required: true,
  },
  {
    parameter: 'FromContact',
    required: true,
  },
  {
    parameter: 'FromTel',
    required: true,
  },
  {
    parameter: 'FromEmail',
    required: false,
  },
  {
    parameter: 'FromAddr1',
    required: true,
  },
  {
    parameter: 'FromAddr2',
    required: false,
  },
  {
    parameter: 'FromCC',
    required: true,
  },
  {
    parameter: 'FromPC',
    required: true,
  },
  {
    parameter: 'FromCity',
    required: true,
  },
  {
    parameter: 'FromProv',
    required: false,
  },
  {
    parameter: 'FromResidential',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'FromIsInside',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'FromIsLimitedAccess',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'FromIsTailGate',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'FromIsTradeShow',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'ToCompany',
    required: true,
  },
  {
    parameter: 'ToContact',
    required: true,
  },
  {
    parameter: 'ToTel',
    required: true,
  },
  {
    parameter: 'ToEmail',
    required: false,
  },
  {
    parameter: 'ToAddr1',
    required: true,
  },
  {
    parameter: 'ToAddr2',
    required: false,
  },
  {
    parameter: 'ToAddr3',
    required: false,
  },
  {
    parameter: 'ToCC',
    required: true,
  },
  {
    parameter: 'ToPC',
    required: true,
  },
  {
    parameter: 'ToCity',
    required: true,
  },
  {
    parameter: 'ToProv',
    required: false,
  },
  {
    parameter: 'ToResidential',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'ToIsInside',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'ToIsLimitedAccess',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'ToIsTailGate',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'ToIsTradeShow',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'ToAppointment',
    required: false,
    defaultValue: 'false',
  },
  {
    parameter: 'ToAppointmentDate',
    required: false,
  },
  {
    parameter: 'ToAppointmentTime',
    required: false,
  },
  {
    parameter: 'Ref',
    required: false,
  },
  {
    parameter: 'Comments',
    required: false,
  },
  {
    parameter: 'emailNotifications',
    required: false,
  },
  ...queryPackageParams,
];

export const historyTableColumns: string[] = [
  'Carrier',
  'ServiceLevel',
  'PackageType',
  'Created',
  'ShipStatus',
  'LastScan',
  'ShipFrom',
  'ShipTo',
];

export const freightServices = [
  'GLSFreight',
  'FedExInternationalEconomyFreight',
  'FedExInternationalPriorityFreight',
  'PurolatorExpeditedLtl',
  'PurolatorStandardLtl',
  'TsDayRossGeneralFreight',
  'TsFastfrateLtl',
  'TsMaritimeOntarioFreight',
  'TsTslLtl',
  'TsGlsFreightGround',
  'TsTruxwebTruxweb',
  'TsTruxwebMorneau',
  'TsTruxwebJfBreaultTransportInc',
  'TsTruxwebTransPlus',
  'TsTruxwebVantageLogistics',
  'TsTruxwebGuilbaultTransport',
  'TsTruxwebJpsLogistiqueInc3pl',
  'TsTruxwebBAndLFreightSolutions',
  'TsTruxwebMinimaxExpress',
  'TsTruxwebLabrancheTransport',
  'TsTruxwebTranskid',
  'TsTruxwebArgusTransport',
  'TsTruxwebTransportBourassa',
  'TsTruxwebCsaTransportation',
  'TsTruxwebMotoTransportation',
  'TsTruxwebPolarisTransportationGroup',
  'TsTruxwebTransportFrontiere',
  'TsTruxwebTransportInterNord',
  'TsTruxwebMessagers',
  'TsTruxwebMorneauBorea',
  'TsTruxwebRegionexTransport',
  'TsTruxwebGlsCanada',
];
